import { useState, useRef, type FormEvent } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Spinner } from '@/components/ui/Spinner'
import { Avatar } from '@/components/ui/Avatar'
import { requireClient } from '@/lib/matrix/client'
import { useRoomStore } from '@/lib/store/room-store'

interface RoomSettingsDialogProps {
  roomId: string
  onClose: () => void
}

export function RoomSettingsDialog({ roomId, onClose }: RoomSettingsDialogProps) {
  const room = useRoomStore((s) => s.rooms.get(roomId))
  const client = requireClient()
  const matrixRoom = client.getRoom(roomId)

  const [name, setName] = useState(room?.name ?? '')
  const [topic, setTopic] = useState(room?.topic ?? '')
  const [inviteUserId, setInviteUserId] = useState('')
  const [saving, setSaving] = useState(false)
  const [inviting, setInviting] = useState(false)
  const [message, setMessage] = useState<{ type: 'ok' | 'error'; text: string } | null>(null)
  const [newAvatarFile, setNewAvatarFile] = useState<File | null>(null)
  const [newAvatarPreview, setNewAvatarPreview] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleSave = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setMessage(null)
    try {
      if (name.trim() !== (room?.name ?? '')) {
        await client.setRoomName(roomId, name.trim())
      }
      if (topic.trim() !== (room?.topic ?? '')) {
        await client.setRoomTopic(roomId, topic.trim())
      }
      if (newAvatarFile) {
        const upload = await client.uploadContent(newAvatarFile, { type: newAvatarFile.type })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (client as any).sendStateEvent(roomId, 'm.room.avatar', { url: upload.content_uri }, '')
        setNewAvatarFile(null)
        setNewAvatarPreview(null)
      }
      setMessage({ type: 'ok', text: 'Room settings updated' })
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to update' })
    } finally {
      setSaving(false)
    }
  }

  const handleInvite = async () => {
    if (!inviteUserId.trim()) return
    setInviting(true)
    setMessage(null)
    try {
      const uid = inviteUserId.trim().startsWith('@') ? inviteUserId.trim() : `@${inviteUserId.trim()}`
      await client.invite(roomId, uid)
      setMessage({ type: 'ok', text: `Invited ${uid}` })
      setInviteUserId('')
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to invite' })
    } finally {
      setInviting(false)
    }
  }

  const powerLevel = matrixRoom?.currentState.getStateEvents('m.room.power_levels', '')
    ?.getContent()?.users?.[client.getUserId()!] ?? 0

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-md bg-[rgb(var(--np-surface))] border border-[rgb(var(--np-border))] rounded-lg p-6 animate-slide-up max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-[rgb(var(--np-tx-primary))] mb-4">Room Settings</h2>

        <form onSubmit={handleSave} className="space-y-4">
          {/* Avatar */}
          <div className="flex items-center gap-4">
            <div className="relative">
              {newAvatarPreview ? (
                <img src={newAvatarPreview} alt="" className="w-16 h-16 rounded-lg object-cover" />
              ) : (
                <Avatar mxc={room?.avatarUrl} name={room?.name ?? ''} userId={roomId} size={64} className="rounded-lg" />
              )}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="absolute -bottom-1 -right-1 p-1 bg-[rgb(var(--np-accent))] text-white rounded-full hover:bg-[rgb(var(--np-accent-muted))] transition-colors"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (f) { setNewAvatarFile(f); setNewAvatarPreview(URL.createObjectURL(f)) }
                  e.target.value = ''
                }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-data text-[rgb(var(--np-tx-muted))] truncate">{roomId}</p>
              {room?.isEncrypted && <p className="text-[10px] text-[rgb(var(--np-status-ok))]">🔒 Encrypted</p>}
            </div>
          </div>

          <Input label="Room Name" value={name} onChange={(e) => setName(e.target.value)} disabled={saving || powerLevel < 50} />
          <div>
            <label className="label-section mb-2 block">Topic</label>
            <textarea
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              rows={2}
              disabled={saving || powerLevel < 50}
              className="w-full bg-[rgb(var(--np-raised))] border border-[rgb(var(--np-border))] rounded-md px-3 py-2.5 text-sm text-[rgb(var(--np-tx-primary))] placeholder-[rgb(var(--np-tx-muted))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--np-accent)/0.5)] transition-all resize-none"
            />
          </div>

          {powerLevel >= 50 && (
            <Button type="submit" disabled={saving} className="w-full">
              {saving ? <Spinner size={14} /> : 'Save Changes'}
            </Button>
          )}
        </form>

        {/* Invite */}
        <div className="mt-6 pt-4 border-t border-[rgb(var(--np-border))]">
          <label className="label-section mb-2 block">Invite User</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={inviteUserId}
              onChange={(e) => setInviteUserId(e.target.value)}
              placeholder="@user:server.com"
              className="flex-1 bg-[rgb(var(--np-raised))] border border-[rgb(var(--np-border))] rounded-md px-3 py-2 text-sm font-data text-[rgb(var(--np-tx-primary))] placeholder-[rgb(var(--np-tx-muted))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--np-accent)/0.5)] transition-all"
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleInvite() } }}
            />
            <Button onClick={handleInvite} disabled={inviting || !inviteUserId.trim()}>
              {inviting ? <Spinner size={14} /> : 'Invite'}
            </Button>
          </div>
        </div>

        {message && (
          <div className={`mt-3 border-l-2 px-4 py-2 rounded-r ${
            message.type === 'ok'
              ? 'border-l-[rgb(var(--np-status-ok))] bg-[rgb(var(--np-status-ok)/0.05)]'
              : 'border-l-[rgb(var(--np-status-critical))] bg-[rgb(var(--np-status-critical)/0.05)]'
          }`}>
            <p className={`text-xs ${message.type === 'ok' ? 'text-[rgb(var(--np-status-ok))]' : 'text-[rgb(var(--np-status-critical))]'}`}>
              {message.text}
            </p>
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  )
}
